"use client"

import { useState, memo } from "react"
import type { ItemDefinition } from "@/types/item.types"

// Rarity color mappings
export const rarityColors: Record<string, string> = {
  common: "text-gray-300",
  rare: "text-blue-400",
  epic: "text-purple-400",
  legendary: "text-amber-400",
  unique: "text-orange-400",
}

export const rarityBorderColors: Record<string, string> = {
  common: "border-gray-500",
  rare: "border-blue-500",
  epic: "border-purple-500",
  legendary: "border-amber-500",
  unique: "border-orange-500",
}

export const rarityGlowColors: Record<string, string> = {
  common: "",
  rare: "shadow-blue-500/30",
  epic: "shadow-purple-500/30",
  legendary: "shadow-amber-500/50",
  unique: "shadow-orange-500/60",
}

export const effectTypeLabels: Record<string, string> = {
  heal: "Heal",
  mana: "Mana",
  "damage-boost": "Damage",
  "speed-boost": "Speed",
  teleport: "Teleport",
  lure: "Lure",
  absorb: "Shield",
  armor: "Armor",
  lifesteal: "Lifesteal",
  "spell-power": "Spell Power",
  "max-health": "Max Health",
  "crit-chance": "Crit Chance",
  "attack-speed": "Attack Speed",
  "cooldown-reduction": "Cooldown",
  "gold-bonus": "Gold Bonus",
  "damage-reflect": "Reflect",
  "mana-regen": "Mana Regen",
  "hp-regen": "HP Regen",
}

// Component to render item icon from individual image file
export const ItemSpriteIcon = memo(function ItemSpriteIcon({
  itemDef,
  size,
}: {
  itemDef: ItemDefinition
  size: number
}) {
  // Use the new image path if available
  if (itemDef.image) {
    return (
      <img
        src={itemDef.image}
        alt={itemDef.name}
        width={size}
        height={size}
        style={{ imageRendering: "pixelated" }}
        className="object-contain"
      />
    )
  }

  // Fallback to colored placeholder
  return (
    <div
      className="rounded"
      style={{
        width: size,
        height: size,
        backgroundColor: itemDef.iconColor,
      }}
    />
  )
})

// Full item card with price and buy button (used in shops)
export const ItemCard = memo(function ItemCard({
  itemDef,
  price,
  canAfford,
  canPickup,
  onBuy,
}: {
  itemDef: ItemDefinition
  price: number
  canAfford: boolean
  canPickup: boolean
  onBuy: () => void
}) {
  const isDisabled = !canAfford || !canPickup

  return (
    <div
      className={`
        relative bg-game-surface/90 rounded-lg border-2 p-3 flex flex-col h-full
        ${rarityBorderColors[itemDef.rarity]}
        ${!isDisabled ? "hover:brightness-110 cursor-pointer" : "opacity-60"}
        transition-all duration-200
        ${rarityGlowColors[itemDef.rarity] ? `shadow-lg ${rarityGlowColors[itemDef.rarity]}` : ""}
      `}
      onClick={() => !isDisabled && onBuy()}
    >
      {/* Item Icon */}
      <div className="flex justify-center mb-2">
        <div className="bg-black/40 rounded-lg p-2">
          <ItemSpriteIcon itemDef={itemDef} size={48} />
        </div>
      </div>

      {/* Item Name */}
      <h4 className={`font-bold text-sm text-center mb-1 ${rarityColors[itemDef.rarity]}`}>
        {itemDef.name}
      </h4>

      {/* Type Badge */}
      <div className="flex justify-center gap-1 mb-2">
        <span className="text-[10px] px-1.5 py-0.5 rounded bg-black/30 text-gray-400 capitalize">
          {itemDef.rarity}
        </span>
        <span className="text-[10px] px-1.5 py-0.5 rounded bg-black/30 text-gray-400 capitalize">
          {itemDef.type}
        </span>
      </div>

      {/* Description */}
      <p className="text-xs text-gray-300 text-center mb-2 leading-tight min-h-[32px]">
        {itemDef.description}
      </p>

      {/* Effects */}
      <div className="border-t border-white/10 pt-2 mb-2 space-y-1 text-xs grow">
        {itemDef.effects.map((effect, idx) => (
          <div key={idx} className="flex justify-between">
            <span className="text-gray-500">{effectTypeLabels[effect.type] || effect.type}</span>
            <span className={effect.value > 0 ? "text-green-400" : "text-red-400"}>
              {effect.type === "lifesteal" || effect.type === "spell-power" || effect.type === "crit-chance" || effect.type === "attack-speed" || effect.type === "cooldown-reduction" || effect.type === "gold-bonus" || effect.type === "damage-reflect"
                ? `${(effect.value * 100).toFixed(0)}%`
                : effect.value > 0
                  ? `+${effect.value}`
                  : effect.value}
            </span>
          </div>
        ))}
        {/* Bonus effects for legendary items - shown in green */}
        {itemDef.bonusEffects && itemDef.bonusEffects.length > 0 && (
          <>
            {itemDef.bonusEffects.map((effect, idx) => (
              <div key={`bonus-${idx}`} className="flex justify-between">
                <span className="text-emerald-500 font-medium">{effectTypeLabels[effect.type] || effect.type}</span>
                <span className="text-emerald-400 font-medium">
                  {effect.type === "lifesteal" || effect.type === "spell-power" || effect.type === "crit-chance" || effect.type === "attack-speed" || effect.type === "cooldown-reduction" || effect.type === "gold-bonus" || effect.type === "damage-reflect"
                    ? `+${(effect.value * 100).toFixed(0)}%`
                    : `+${effect.value}`}
                </span>
              </div>
            ))}
          </>
        )}
      </div>

      {/* Buy Button - Always bottom aligned and full width */}
      <button
        disabled={isDisabled}
        onClick={(e) => {
          e.stopPropagation()
          if (!isDisabled) onBuy()
        }}
        className={`
          w-full py-2 rounded-lg font-bold text-sm flex items-center justify-center gap-2 mt-auto
          ${canAfford && canPickup
            ? "bg-game-gold/20 hover:bg-game-gold/30 text-game-gold border border-game-gold/50 cursor-pointer"
            : "bg-red-900/20 text-red-400 border border-red-600/50 cursor-not-allowed"}
          transition-colors
        `}
      >
        <span>{price}</span>
        <img
          src="/images/icon-coins.png"
          alt="gold"
          className="w-4 h-4"
          style={{ imageRendering: "pixelated" }}
        />
      </button>

      {/* Inventory full warning */}
      {!canPickup && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/70 rounded-lg">
          <span className="text-red-400 text-sm font-bold">Inventory Full</span>
        </div>
      )}
    </div>
  )
})

// Compact item display card for pelf rewards (no price/buy button)
export const ItemRewardCard = memo(function ItemRewardCard({
  itemDef,
  showParticles = false,
  message,
}: {
  itemDef: ItemDefinition
  showParticles?: boolean
  message?: string
}) {
  return (
    <div
      className={`
        relative bg-game-surface/90 rounded-lg border-2 p-4
        ${rarityBorderColors[itemDef.rarity]}
        ${rarityGlowColors[itemDef.rarity] ? `shadow-lg ${rarityGlowColors[itemDef.rarity]}` : ""}
      `}
    >
      {/* Particle effect overlay */}
      {showParticles && (
        <div className="absolute inset-0 overflow-hidden rounded-lg pointer-events-none">
          {/* Sparkle particles */}
          {Array.from({ length: 12 }).map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-white rounded-full animate-sparkle"
              style={{
                left: `${20 + Math.random() * 60}%`,
                top: `${20 + Math.random() * 60}%`,
                animationDelay: `${i * 0.15}s`,
                animationDuration: `${1.5 + Math.random() * 0.5}s`,
              }}
            />
          ))}
          {/* Glow shimmer */}
          <div
            className="absolute inset-0 opacity-20 animate-shimmer"
            style={{
              background: `linear-gradient(45deg, transparent 0%, ${
                itemDef.rarity === "unique" ? "rgba(249, 115, 22, 0.5)" :
                itemDef.rarity === "legendary" ? "rgba(251, 191, 36, 0.4)" :
                itemDef.rarity === "epic" ? "rgba(168, 85, 247, 0.4)" :
                itemDef.rarity === "rare" ? "rgba(59, 130, 246, 0.4)" :
                "rgba(255, 255, 255, 0.2)"
              } 50%, transparent 100%)`,
            }}
          />
        </div>
      )}

      {/* Item Icon */}
      <div className="flex justify-center mb-3">
        <div className="bg-black/40 rounded-lg p-3">
          <ItemSpriteIcon itemDef={itemDef} size={64} />
        </div>
      </div>

      {/* Item Name */}
      <h4 className={`font-bold text-base text-center mb-1 ${rarityColors[itemDef.rarity]}`}>
        {itemDef.name}
      </h4>

      {/* Rarity Badge */}
      <div className="flex justify-center gap-1 mb-2">
        <span className={`text-xs px-2 py-0.5 rounded bg-black/30 capitalize ${rarityColors[itemDef.rarity]}`}>
          {itemDef.rarity}
        </span>
      </div>

      {/* Description */}
      <p className="text-xs text-gray-300 text-center mb-2 leading-tight">
        {itemDef.description}
      </p>

      {/* Effects */}
      <div className="border-t border-white/10 pt-2 space-y-1 text-xs">
        {itemDef.effects.map((effect, idx) => (
          <div key={idx} className="flex justify-between">
            <span className="text-gray-500">{effectTypeLabels[effect.type] || effect.type}</span>
            <span className={effect.value > 0 ? "text-green-400" : "text-red-400"}>
              {effect.type === "lifesteal" || effect.type === "spell-power" || effect.type === "crit-chance" || effect.type === "attack-speed" || effect.type === "cooldown-reduction" || effect.type === "gold-bonus" || effect.type === "damage-reflect"
                ? `${(effect.value * 100).toFixed(0)}%`
                : effect.value > 0
                  ? `+${effect.value}`
                  : effect.value}
            </span>
          </div>
        ))}
        {/* Bonus effects for legendary items - shown in green */}
        {itemDef.bonusEffects && itemDef.bonusEffects.length > 0 && (
          <>
            {itemDef.bonusEffects.map((effect, idx) => (
              <div key={`bonus-${idx}`} className="flex justify-between">
                <span className="text-emerald-500 font-medium">{effectTypeLabels[effect.type] || effect.type}</span>
                <span className="text-emerald-400 font-medium">
                  {effect.type === "lifesteal" || effect.type === "spell-power" || effect.type === "crit-chance" || effect.type === "attack-speed" || effect.type === "cooldown-reduction" || effect.type === "gold-bonus" || effect.type === "damage-reflect"
                    ? `+${(effect.value * 100).toFixed(0)}%`
                    : `+${effect.value}`}
                </span>
              </div>
            ))}
          </>
        )}
      </div>

      {/* Status message */}
      {message && (
        <div className="mt-3 pt-2 border-t border-white/10">
          <p className={`text-xs text-center font-medium ${
            message.includes("full") ? "text-amber-400" : "text-green-400"
          }`}>
            {message}
          </p>
        </div>
      )}
    </div>
  )
})

// Compact item card for shop window (smaller, fits in wooden frame)
export const ShopItemCard = memo(function ShopItemCard({
  itemDef,
  price,
  canAfford,
  canPickup,
  onBuy,
  isSpinning = false,
  spinDelay = 0,
  isMobile = false,
}: {
  itemDef: ItemDefinition
  price: number
  canAfford: boolean
  canPickup: boolean
  onBuy: () => void
  isSpinning?: boolean
  spinDelay?: number
  isMobile?: boolean
}) {
  const isDisabled = !canAfford || !canPickup
  const [showTooltip, setShowTooltip] = useState(false)
  const [isHovering, setIsHovering] = useState(false)

  // On desktop, show tooltip on hover; on mobile, show on (i) button click
  const tooltipVisible = isMobile ? showTooltip : isHovering

  // Card dimensions: 20% smaller on mobile (80% of original)
  const cardWidth = isMobile ? 97 : 121
  const cardHeight = isMobile ? 160 : 200
  const iconSize = isMobile ? 26 : 32

  return (
    <div
      className="relative"
      style={{
        width: cardWidth,
        height: cardHeight,
      }}
      onMouseEnter={() => !isMobile && setIsHovering(true)}
      onMouseLeave={() => !isMobile && setIsHovering(false)}
    >
      {/* Tooltip - positioned below the card with diamond pointer */}
      {tooltipVisible && (
        <div
          className="absolute left-1/2 -translate-x-1/2 top-full mt-2 bg-black border border-gray-500 rounded-md p-3 z-200 text-[10px] shadow-xl text-center"
          style={{ width: 180 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Diamond pointer pointing UP at the card */}
          <div className="absolute left-1/2 -translate-x-1/2 -top-2 w-3 h-3 bg-black border-l border-t border-gray-500 rotate-45" />
          <h4 className={`font-bold text-sm mb-1 ${rarityColors[itemDef.rarity]}`}>{itemDef.name}</h4>
          <p className="text-white mb-2">{itemDef.description}</p>
          <div className="space-y-1 border-t border-white/20 pt-2">
            {itemDef.effects.map((effect, idx) => (
              <div key={idx} className="flex justify-between">
                <span className="text-white/70">{effectTypeLabels[effect.type] || effect.type}</span>
                <span className={effect.value > 0 ? "text-green-400" : "text-red-400"}>
                  {effect.type === "lifesteal" || effect.type === "spell-power" || effect.type === "crit-chance" || effect.type === "attack-speed" || effect.type === "cooldown-reduction" || effect.type === "gold-bonus"
                    ? `${(effect.value * 100).toFixed(0)}%`
                    : effect.value > 0
                      ? `+${effect.value}`
                      : effect.value}
                </span>
              </div>
            ))}
            {itemDef.bonusEffects && itemDef.bonusEffects.map((effect, idx) => (
              <div key={`bonus-${idx}`} className="flex justify-between">
                <span className="text-emerald-400">{effectTypeLabels[effect.type] || effect.type}</span>
                <span className="text-emerald-400">
                  {effect.type === "lifesteal" || effect.type === "spell-power" || effect.type === "crit-chance" || effect.type === "attack-speed" || effect.type === "cooldown-reduction" || effect.type === "gold-bonus" || effect.type === "damage-reflect"
                    ? `+${(effect.value * 100).toFixed(0)}%`
                    : `+${effect.value}`}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Card content - opacity affects this but not tooltip */}
      <div
        className={`
          absolute inset-0 bg-game-surface/95 rounded-md border-2 p-2 flex flex-col
          ${rarityBorderColors[itemDef.rarity]}
          ${!isDisabled ? "hover:brightness-110 cursor-pointer" : "opacity-60"}
          transition-all duration-200
          ${rarityGlowColors[itemDef.rarity] ? `shadow-md ${rarityGlowColors[itemDef.rarity]}` : ""}
          ${isSpinning ? "animate-slot-spin" : ""}
        `}
        style={{
          animationDelay: isSpinning ? `${spinDelay}ms` : undefined,
        }}
        onClick={() => !isDisabled && !isSpinning && onBuy()}
      >
        {/* Info icon for mobile tooltip only */}
        {isMobile && (
          <button
            onClick={(e) => {
              e.stopPropagation()
              setShowTooltip(!showTooltip)
            }}
            className="absolute top-1 right-1 w-4 h-4 rounded-full bg-black/60 text-gray-400 text-[10px] flex items-center justify-center hover:bg-black/80 hover:text-white z-10"
          >
            i
          </button>
        )}

        {/* Item Icon */}
        <div className="flex justify-center mb-1">
          <div className="bg-black/40 rounded p-1">
            <ItemSpriteIcon itemDef={itemDef} size={iconSize} />
          </div>
        </div>

        {/* Item Name */}
        <h4 className={`font-bold text-[10px] text-center leading-tight mb-1 ${rarityColors[itemDef.rarity]}`}>
          {itemDef.name}
        </h4>

        {/* Type Badge */}
        <div className="flex justify-center gap-0.5 mb-1">
          <span className="text-[8px] px-1 py-0.5 rounded bg-black/30 text-gray-400 capitalize">
            {itemDef.rarity}
          </span>
        </div>

        {/* Effects (compact) */}
        {(itemDef.effects.length > 0 || (itemDef.bonusEffects && itemDef.bonusEffects.length > 0)) && (
          <div className="border-t border-white/10 pt-1 mb-1 space-y-0.5 text-[8px] grow">
            {itemDef.effects.slice(0, 2).map((effect, idx) => (
              <div key={idx} className="flex justify-between">
                <span className="text-gray-500">{effectTypeLabels[effect.type] || effect.type}</span>
                <span className={effect.value > 0 ? "text-green-400" : "text-red-400"}>
                  {effect.type === "lifesteal" || effect.type === "spell-power" || effect.type === "crit-chance" || effect.type === "attack-speed" || effect.type === "cooldown-reduction" || effect.type === "gold-bonus"
                    ? `${(effect.value * 100).toFixed(0)}%`
                    : effect.value > 0
                      ? `+${effect.value}`
                      : effect.value}
                </span>
              </div>
            ))}
            {/* Show bonus effects in compact view */}
            {itemDef.bonusEffects && itemDef.bonusEffects.slice(0, 1).map((effect, idx) => (
              <div key={`bonus-${idx}`} className="flex justify-between">
                <span className="text-emerald-500">{effectTypeLabels[effect.type] || effect.type}</span>
                <span className="text-emerald-400">
                  {effect.type === "lifesteal" || effect.type === "spell-power" || effect.type === "crit-chance" || effect.type === "attack-speed" || effect.type === "cooldown-reduction" || effect.type === "gold-bonus" || effect.type === "damage-reflect"
                    ? `+${(effect.value * 100).toFixed(0)}%`
                    : `+${effect.value}`}
                </span>
              </div>
            ))}
            {(itemDef.effects.length > 2 || (itemDef.bonusEffects && itemDef.bonusEffects.length > 1)) && (
              <div className="text-gray-500 text-center">+{itemDef.effects.length - 2 + (itemDef.bonusEffects?.length || 0) - 1} more</div>
            )}
          </div>
        )}

        {/* Buy Button */}
        <button
          disabled={isDisabled || isSpinning}
          onClick={(e) => {
            e.stopPropagation()
            if (!isDisabled && !isSpinning) onBuy()
          }}
          className={`
            w-full py-1 rounded font-bold text-[10px] flex items-center justify-center gap-1 mt-auto
            ${canAfford && canPickup
              ? "bg-game-gold/20 hover:bg-game-gold/30 text-game-gold border border-game-gold/50"
              : "bg-red-900/20 text-red-400 border border-red-600/50 cursor-not-allowed"}
            transition-colors
          `}
        >
          <span>{price}</span>
          <img
            src="/images/icon-coins.png"
            alt="gold"
            className="w-3 h-3"
            style={{ imageRendering: "pixelated" }}
          />
        </button>

        {/* Inventory full warning */}
        {!canPickup && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/70 rounded-md">
            <span className="text-red-400 text-[9px] font-bold">Full</span>
          </div>
        )}
      </div>
    </div>
  )
})
